import componentBattery from "~/assets/photo/solar-proposal/component-battery.png"
import componentInverter from "~/assets/photo/solar-proposal/component-inverter.png"
import componentPanels from "~/assets/photo/solar-proposal/component-panels.jpg"
import homeAerial from "~/assets/photo/solar-proposal/home-aerial.png"

import type {
  SolarPresentationProposal,
  SolarSavingsScenario,
} from "./solar-presentation"
import { toSolarImpact } from "./solar-proposal-impact"

/**
 * The design reference rendered at `/solar-proposal/sample`. Every figure is
 * the Claude Design sample, not a calculation; real proposals read every
 * section from REST. The savings builder below plays the server's part for the
 * dial's what-if points.
 */

const SOLAR_MONTHLY_PAYMENT = 154
const ESCALATOR_PCT = 3.5
const TERM_YEARS = 25

const buildScenario = (monthlyBill: number): SolarSavingsScenario => {
  let cumulativeUtility = 0
  let cumulativeSolar = 0
  let breakEvenYear: number | null = null
  const years = Array.from({ length: TERM_YEARS }, (_, index) => {
    const year = index + 1
    cumulativeUtility +=
      monthlyBill * 12 * (1 + ESCALATOR_PCT / 100) ** (year - 1)
    cumulativeSolar += SOLAR_MONTHLY_PAYMENT * 12
    if (breakEvenYear == null && cumulativeUtility >= cumulativeSolar) {
      breakEvenYear = year
    }
    return {
      year,
      cumulativeUtility: Math.round(cumulativeUtility),
      cumulativeSolar: Math.round(cumulativeSolar),
    }
  })
  const totalUtility = Math.round(cumulativeUtility)
  const totalSolar = Math.round(cumulativeSolar)

  return {
    monthlyBill,
    years,
    totalUtility,
    totalSolar,
    totalSavings: totalUtility - totalSolar,
    breakEvenYear,
  }
}

const SCENARIO_BILLS = Array.from({ length: 521 }, (_, index) => 80 + index)

export const SOLAR_PROPOSAL_FIXTURE: SolarPresentationProposal = {
  id: `PWR-8820-41A`,
  proposalNumber: `PWR-8820-41A`,
  statusLabel: `Approved · ready to sign`,
  createdAt: `2026-04-14T12:00:00.000Z`,
  homeowner: {
    name: `Maria & David Lopez`,
    email: null,
    phone: null,
  },
  familyName: `Lopez`,
  consultant: {
    name: `J. Alvarez`,
    email: `j.alvarez@powur.com`,
    phone: `(408) 555-0142`,
  },
  address: {
    street: `1428 Marigold Ave`,
    city: `San Jose`,
    state: `CA`,
    postal: `95127`,
  },
  residence: {
    familyName: `Lopez`,
    address: {
      street: `1428 Marigold Ave`,
      city: `San Jose`,
      state: `CA`,
      postal: `95127`,
    },
    siteDetails: [`PG&E`, `E-TOU-C`, `Composition roof`, `S/W exposure`],
    image: {
      url: homeAerial,
      kind: `site_imagery`,
      width: null,
      height: null,
    },
    layout: null,
  },
  utilityName: `PG&E`,
  offsetPct: 102,
  offsetIsAurora: false,
  system: {
    hasStorage: true,
    specs: [
      {
        label: `System size`,
        value: `8.4 kW`,
        sub: `Direct-current capacity`,
        icon: `panel`,
      },
      {
        label: `Energy offset`,
        value: `102%`,
        sub: `of your annual usage`,
        icon: `sun`,
      },
      {
        label: `Production`,
        value: `12,480 kWh`,
        sub: `per year · 1.48 kWh/W ratio`,
        icon: `bolt`,
      },
      {
        label: `Storage`,
        value: `10.08 kWh`,
        sub: `IQ Battery 10 · backup-ready`,
        icon: `battery`,
      },
      {
        label: `Number of panels`,
        value: `24`,
        sub: `LG NeON® H 350W modules`,
        icon: `grid`,
      },
    ],
  },
  components: [
    {
      id: `panels`,
      kind: `Panels`,
      name: `LG NeON® H 350W`,
      highlight: `NeON® H`,
      tag: `Tier-1 monocrystalline panel · 24-panel array`,
      image: componentPanels,
      imageAlt: `LG NeON® H 350W`,
      imageScale: 0.72,
      summary: `LG's best-selling solar module — one of the most powerful and versatile on the market today. Featuring Cello Technology and 60 high-efficiency cells, the NeON® H squeezes more kilowatts out of every square foot of your roof.`,
      stats: [
        { value: `350W`, label: `Per panel` },
        { value: `22.1%`, label: `Efficiency` },
        { value: `25 yr`, label: `Warranty` },
      ],
      features: [
        {
          title: `25-year performance warranty`,
          body: `Guaranteed to perform at minimum 89.6% of initial output after 25 years.`,
        },
        {
          title: `All-black aesthetics`,
          body: `Thin wires that read as one continuous surface from the curb — modern, low-profile.`,
        },
        {
          title: `Better in real heat`,
          body: `Improved temperature coefficient keeps output high on the days you actually need it.`,
        },
      ],
    },
    {
      id: `inverter`,
      kind: `Inverter`,
      name: `Enphase IQ8+ Microinverter`,
      highlight: `IQ8+`,
      tag: `Grid-forming microinverter · One per panel`,
      image: componentInverter,
      imageAlt: `Enphase IQ8+ Microinverter`,
      imageScale: 1,
      summary: `A microinverter on every panel — so a single shaded module doesn't drag the rest of the array down. Enphase's IQ8 generation also has Sunlight Backup™, which keeps essentials running during the day even when the grid is out.`,
      stats: [
        { value: `97.5%`, label: `CEC efficiency` },
        { value: `240V`, label: `Output AC` },
        { value: `25 yr`, label: `Warranty` },
      ],
      features: [
        {
          title: `Module-level optimization`,
          body: `Each panel performs at its own peak. Shading on one doesn't penalize the system.`,
        },
        {
          title: `Sunlight Backup™`,
          body: `Stay powered during daytime outages — no battery required for daylight resilience.`,
        },
        {
          title: `25-year warranty`,
          body: `The IQ8 is the longest-warrantied inverter Enphase has ever shipped.`,
        },
      ],
    },
    {
      id: `storage`,
      kind: `Storage`,
      name: `Enphase IQ Battery 10`,
      highlight: `IQ Battery 10`,
      tag: `Lithium-iron-phosphate (LFP) home battery`,
      image: componentBattery,
      imageAlt: `Enphase IQ Battery 10`,
      imageScale: 1,
      summary: `10.08 kWh of storage that pairs natively with the IQ8 microinverters. LFP chemistry gives it longer cycle life and a safer thermal profile than older nickel-cobalt batteries.`,
      stats: [
        { value: `10.08 kWh`, label: `Usable capacity` },
        { value: `3.84 kW`, label: `Continuous` },
        { value: `10 yr`, label: `Warranty` },
      ],
      features: [
        {
          title: `Whole-home backup`,
          body: `Power the essentials — fridge, lights, Wi-Fi, well pump — through a multi-day outage.`,
        },
        {
          title: `Time-of-use savings`,
          body: `Charge when energy is cheap, discharge when utility rates peak. Bill goes down further.`,
        },
        {
          title: `Safer LFP chemistry`,
          body: `No cobalt, no thermal runaway, and a longer cycle life than traditional Li-ion batteries.`,
        },
      ],
    },
  ],
  savings: {
    utilityName: `PG&E`,
    solarMonthlyPayment: SOLAR_MONTHLY_PAYMENT,
    termYears: TERM_YEARS,
    escalatorPct: ESCALATOR_PCT,
    defaultMonthlyBill: 298,
    scenarios: SCENARIO_BILLS.map(buildScenario),
    monthlyProduction: [
      { month: `Jan`, kwh: 820 },
      { month: `Feb`, kwh: 780 },
      { month: `Mar`, kwh: 870 },
      { month: `Apr`, kwh: 990 },
      { month: `May`, kwh: 1120 },
      { month: `Jun`, kwh: 1350 },
      { month: `Jul`, kwh: 1320 },
      { month: `Aug`, kwh: 1280 },
      { month: `Sep`, kwh: 1100 },
      { month: `Oct`, kwh: 960 },
      { month: `Nov`, kwh: 870 },
      { month: `Dec`, kwh: 830 },
    ],
  },
  financeOptions: [
    {
      id: `goodleap`,
      partnerName: `GoodLeap`,
      productName: `25-year solar loan`,
      terms: `3.99% APR · $0 down · ITC applied at month 18`,
      payment: 154,
      paymentCadence: `monthly`,
      totalCost: 46200,
      lifetimeSavings: 68420,
      monthlySaved: 144,
      breakEvenYear: 7,
      highlights: [
        `$0 down at signing`,
        `Soft credit check — no impact`,
        `30% federal ITC applied at month 18`,
        `Pre-payable anytime without penalty`,
      ],
    },
    {
      id: `sungage`,
      partnerName: `Sungage`,
      productName: `20-year solar + storage loan`,
      terms: `5.49% APR · $0 down · Fixed payment`,
      payment: 189,
      paymentCadence: `monthly`,
      totalCost: 45360,
      lifetimeSavings: 65860,
      monthlySaved: 109,
      breakEvenYear: 8,
      highlights: [
        `$0 down at signing`,
        `Fixed payment for the full term`,
        `Storage financed with the system`,
      ],
    },
    {
      id: `lightreach`,
      partnerName: `LightReach`,
      productName: `PPA · $0.149/kWh · 2.9% escalator`,
      terms: `25-year term · LightReach owns and maintains the system`,
      payment: 155,
      paymentCadence: `monthly`,
      totalCost: 46480,
      lifetimeSavings: 64745,
      monthlySaved: 143,
      breakEvenYear: 1,
      highlights: [
        `No upfront cost`,
        `Monitoring and maintenance included`,
        `Pay only for the energy produced`,
      ],
    },
    {
      id: `cash`,
      partnerName: `Cash`,
      productName: `Pay in full`,
      terms: `Break-even year 7 · best long-term ROI`,
      payment: 42800,
      paymentCadence: `upfront`,
      totalCost: 42800,
      lifetimeSavings: 68420,
      monthlySaved: null,
      breakEvenYear: 7,
      highlights: [
        `Own the system outright`,
        `30% federal ITC claimed on your return`,
        `No interest, no term`,
      ],
    },
  ],
  defaultFinanceOptionId: `goodleap`,
  pricing: [
    { label: `Equipment & installation`, amount: 36940, credit: false },
    { label: `Adders (battery + MPU)`, amount: 11200, credit: false },
    { label: `Dealer fee`, amount: 4420, credit: false },
    { label: `Sales tax`, amount: 1240, credit: false },
    { label: `Federal ITC (30%)`, amount: 10800, credit: true },
  ],
  netCost: 42800,
  // As the impact read serves a non-TPO system with ~580 MWh of 25-year production.
  impact: toSolarImpact({
    co2MetricTons25yr: 224,
    treesEquivalent: 374,
    homeValueAdded: 51240,
    isTpo: false,
  }),
}
